import { Helmet } from '@lib/helmet'

export interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'profile'
  siteName?: string
  author?: string
  publishedTime?: string
  modifiedTime?: string
  section?: string
  tags?: string[]
  noIndex?: boolean
}

const DEFAULT_SEO = {
  title: 'SpeakDuo - English Speaking Club with AI Feedback',
  description:
    'Join SpeakDuo, an online English conversation community with AI corrections. Connect, improve, and enjoy speaking practice tailored to your goals. Available 7 days a week.',
  keywords:
    'English speaking club, AI feedback, conversation practice, language learning, English improvement, speaking skills, online English tutoring',
  image: 'https://www.speakduo.com/og-image.jpg',
  url: 'https://www.speakduo.com',
  type: 'website' as const,
  siteName: 'SpeakDuo',
  author: 'SpeakDuo Team',
}

export const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  siteName,
  author,
  publishedTime,
  modifiedTime,
  section,
  tags,
  noIndex = false,
}: SEOProps) => {
  const seo = {
    title: title ? `${title} | SpeakDuo` : DEFAULT_SEO.title,
    description: description || DEFAULT_SEO.description,
    keywords: keywords || DEFAULT_SEO.keywords,
    image: image || DEFAULT_SEO.image,
    url: url || DEFAULT_SEO.url,
    type,
    siteName: siteName || DEFAULT_SEO.siteName,
    author: author || DEFAULT_SEO.author,
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seo.title}</title>
      <meta name='description' content={seo.description} />
      <meta name='keywords' content={seo.keywords} />
      <meta name='author' content={seo.author} />

      {/* Robots */}
      <meta
        name='robots'
        content={
          noIndex
            ? 'noindex, nofollow'
            : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        }
      />

      {/* Canonical URL */}
      <link rel='canonical' href={seo.url} />

      {/* Open Graph */}
      <meta property='og:type' content={seo.type} />
      <meta property='og:title' content={seo.title} />
      <meta property='og:description' content={seo.description} />
      <meta property='og:image' content={seo.image} />
      <meta property='og:url' content={seo.url} />
      <meta property='og:site_name' content={seo.siteName} />
      <meta property='og:locale' content='en_US' />

      {/* Article specific Open Graph tags */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property='article:published_time' content={publishedTime} />}
          {modifiedTime && <meta property='article:modified_time' content={modifiedTime} />}
          {author && <meta property='article:author' content={author} />}
          {section && <meta property='article:section' content={section} />}
          {tags && tags.map((tag) => <meta key={tag} property='article:tag' content={tag} />)}
        </>
      )}

      {/* Twitter Cards */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={seo.title} />
      <meta name='twitter:description' content={seo.description} />
      <meta name='twitter:image' content={seo.image} />
      <meta name='twitter:url' content={seo.url} />

      {/* Additional SEO */}
      <meta name='language' content='English' />
      <meta name='revisit-after' content='7 days' />
      <meta name='distribution' content='global' />
      <meta name='rating' content='general' />
    </Helmet>
  )
}

export default SEO
